import { isInsightRecord } from './insightValue'

export interface NormalizedListResponse<T> {
  items: T[]
  total: number
}

export interface NormalizedPaginatedResponse<T> {
  results: T[]
  count: number
  limit: number
  offset: number
}

function finiteCount(value: unknown): number | null {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

function finiteInteger(value: unknown): number | null {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null
}

function unwrapContainer(value: unknown): unknown {
  const root = isInsightRecord(value) ? value : null
  if (!root) return value
  const directKeys = ['results', 'configs', 'items', 'total', 'count']
  if (directKeys.some((key) => key in root)) return root
  return root['data'] ?? root
}

/** Normalizes the measured legacy/admin list envelopes at one pure boundary. */
export function normalizeListResponse<T>(value: unknown): NormalizedListResponse<T> {
  const container = unwrapContainer(value)
  const record = isInsightRecord(container) ? container : null
  const nestedData = record?.['data']
  const items = Array.isArray(record?.['results'])
    ? (record['results'] as T[])
    : Array.isArray(record?.['configs'])
      ? (record['configs'] as T[])
      : Array.isArray(record?.['items'])
        ? (record['items'] as T[])
        : Array.isArray(nestedData)
          ? (nestedData as T[])
          : Array.isArray(container)
            ? (container as T[])
            : []
  return {
    items,
    total: finiteCount(record?.['total'] ?? record?.['count']) ?? items.length,
  }
}

/**
 * Maps the measured flat list variants (`results`/`configs`/`items`) onto the v4 pagination
 * contract. `total` remains accepted because the live Insights backend retains it for v3 while
 * `count` is the v4 canonical field.
 */
export function normalizePaginatedResponse<T>(
  value: unknown,
  fallback: { limit?: number; offset?: number } = {},
): NormalizedPaginatedResponse<T> {
  const normalized = normalizeListResponse<T>(value)
  const container = unwrapContainer(value)
  const record = isInsightRecord(container) ? container : null
  return {
    results: normalized.items,
    count: normalized.total,
    limit: finiteInteger(record?.['limit']) ?? fallback.limit ?? normalized.items.length,
    offset: finiteInteger(record?.['offset']) ?? fallback.offset ?? 0,
  }
}
